import { ComponentType, useCallback, useEffect } from 'react';

import { Link, Progress } from '@backstage/core-components';
import { IconComponent, errorApiRef, useApi } from '@backstage/core-plugin-api';
import { useEntityList } from '@backstage/plugin-catalog-react';
import {
  TemplateEntityV1beta3,
  isTemplateEntityV1beta3,
} from '@backstage/plugin-scaffolder-common';
import { TemplateGroupFilter } from '@backstage/plugin-scaffolder-react';
import { TemplateGroup } from '@backstage/plugin-scaffolder-react/alpha';
import Typography from '@material-ui/core/Typography';

import { compareTemplatesAlphabetically } from '../../utils/compareTemplatesAlphabetically';

export interface AlphabeticalTemplateGroupsProps {
  groups: TemplateGroupFilter[];
  templateFilter?: (entity: TemplateEntityV1beta3) => boolean;
  TemplateCardComponent?: ComponentType<{
    template: TemplateEntityV1beta3;
  }>;
  onTemplateSelected?: (template: TemplateEntityV1beta3) => void;
  additionalLinksForEntity?: (template: TemplateEntityV1beta3) => {
    icon: IconComponent;
    text: string;
    url: string;
  }[];
}

/**
 * Renders scaffolder template groups with templates sorted alphabetically by title.
 */
export const AlphabeticalTemplateGroups = (
  props: AlphabeticalTemplateGroupsProps,
) => {
  const { loading, error, entities } = useEntityList();
  const {
    groups,
    templateFilter,
    TemplateCardComponent,
    onTemplateSelected,
    additionalLinksForEntity,
  } = props;
  const errorApi = useApi(errorApiRef);
  const onSelected = useCallback(
    (template: TemplateEntityV1beta3) => {
      onTemplateSelected?.(template);
    },
    [onTemplateSelected],
  );

  useEffect(() => {
    if (error) {
      errorApi.post(error);
    }
  }, [error, errorApi]);

  if (loading) {
    return <Progress />;
  }

  if (error) {
    return null;
  }

  if (!entities?.length) {
    return (
      <Typography variant="body2">
        No templates found that match your filter. Learn more about{' '}
        <Link to="https://backstage.io/docs/features/software-templates/adding-templates">
          adding templates
        </Link>
        .
      </Typography>
    );
  }

  return (
    <>
      {groups.map(({ title, filter }) => {
        const templates = entities
          .filter(isTemplateEntityV1beta3)
          .filter(e => (templateFilter ? templateFilter(e) : true))
          .filter(filter)
          .sort(compareTemplatesAlphabetically)
          .map(template => {
            const additionalLinks = additionalLinksForEntity?.(template) ?? [];

            return {
              template,
              additionalLinks,
            };
          });

        return (
          <TemplateGroup
            key={String(title)}
            templates={templates}
            title={title}
            components={{ CardComponent: TemplateCardComponent }}
            onSelected={onSelected}
          />
        );
      })}
    </>
  );
};
